/**
 * Centralized API service with interceptors
 * Handles token management, error handling, and request/response processing
 */

const baseUrl = process.env.REACT_APP_API_URL;

/**
 * Get authentication token from localStorage
 */
const getAuthToken = () => localStorage.getItem('accessToken');

/**
 * Default headers for API requests
 */
const getDefaultHeaders = (includeAuth = true) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
};

/**
 * Handle API response
 * @param {Response} response - Fetch API response
 * @returns {Promise<any>} Parsed response data
 */
const handleResponse = async (response) => {
  let data;
  
  try {
    data = await response.json();
  } catch (error) {
    // Response might not be JSON
    data = null;
  }

  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `HTTP ${response.status}: ${response.statusText}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

/**
 * Make API request
 * @param {string} endpoint - API endpoint (relative to baseUrl)
 * @param {object} options - Fetch options
 * @returns {Promise<any>} Response data
 */
export const apiRequest = async (endpoint, options = {}) => {
  const { requireAuth = true, ...fetchOptions } = options;

  const config = {
    ...fetchOptions,
    headers: {
      ...getDefaultHeaders(requireAuth),
      ...fetchOptions.headers,
    },
  };

  try {
    const response = await fetch(`${baseUrl}${endpoint}`, config);
    return await handleResponse(response);
  } catch (error) {
    // Log error for debugging (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.error('API Request Error:', {
        endpoint,
        error: error.message,
        status: error.status,
      });
    }
    throw error;
  }
};

/**
 * GET request
 */
export const get = (endpoint, options = {}) => {
  return apiRequest(endpoint, { ...options, method: 'GET' });
};

/**
 * POST request
 */
export const post = (endpoint, body, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * PUT request
 */
export const put = (endpoint, body, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

/**
 * DELETE request
 */
export const del = (endpoint, body = null, options = {}) => {
  const config = { ...options, method: 'DELETE' };
  if (body) {
    config.body = JSON.stringify(body);
  }
  return apiRequest(endpoint, config);
};

/**
 * POST request with form data (for login)
 */
export const postFormData = async (endpoint, formData, options = {}) => {
  const config = {
    ...options,
    method: 'POST',
    headers: {
      ...options.headers,
    },
    body: formData,
  };

  try {
    const response = await fetch(`${baseUrl}${endpoint}`, config);
    return await handleResponse(response);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('API Request Error:', {
        endpoint,
        error: error.message,
        status: error.status,
      });
    }
    throw error;
  }
};

const apiService = {
  get,
  post,
  put,
  delete: del,
  postFormData,
};

export default apiService;

